import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { onAnimationEnd } from '@theme/utilities';

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {Object} [featured_media] - Featured media object
 * @property {Object} [featured_media.preview_image] - Preview image data
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 */

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLElement} quantityDisplay - Quantity display container
 * @property {HTMLElement} quantityNumber - Quantity number element
 * @property {HTMLImageElement} productImage - Product image element
 */

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Shows when the main buy buttons scroll out of view.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton', 'quantityDisplay', 'quantityNumber'];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #mainBottomObserver = null;

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {boolean} */
  #hiddenByBottom = false;

  /** @type {MutationObserver | null} */
  #mainButtonObserver = null;

  /** @type {number | null} */
  #syncFrame = null;

  /** @type {HTMLElement | null} */
  #chatWidget = null;

  /** Height the bar has grown by to line the button up with the chat widget. */
  #chatLift = 0;

  /** @type {ResizeObserver | null} */
  #chatResizeObserver = null;

  /** @type {MutationObserver | null} */
  #chatMutationObserver = null;

  /** @type {number | undefined} */
  #chatPollTimer;

  /** True from a tap that submitted the product form until the cart answers. */
  #pendingAdd = false;

  /** @type {number | undefined} */
  #pendingAddTimeout;

  connectedCallback() {
    super.connectedCallback();

    this.#setupIntersectionObserver();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, { signal });
    document.addEventListener(ThemeEvents.cartError, this.#handleCartError, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });

    this.#getInitialQuantity();
    this.#observeMainButton();
    this.#syncFromMainButton();
    this.#syncSizeState();
    this.#watchChatWidget();
    window.addEventListener('resize', this.#syncChatClearance, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    this.#mainButtonObserver?.disconnect();
    this.#chatResizeObserver?.disconnect();
    this.#chatMutationObserver?.disconnect();
    clearInterval(this.#chatPollTimer);
    document.documentElement.removeAttribute('data-sticky-atc-visible');
    if (this.#syncFrame) cancelAnimationFrame(this.#syncFrame);
    clearTimeout(this.#pendingAddTimeout);
    this.#abortController.abort();
    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }
  }

  /**
   * Sets up the IntersectionObserver to watch the buy buttons visibility
   */
  #setupIntersectionObserver() {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    const buyButtonsBlock = productForm.closest('.buy-buttons-block');
    if (!buyButtonsBlock) return;

    // In themes migrated from 2.0, the footer element doesn't exist
    const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
    if (!footer) return;

    // Observer for buy buttons visibility
    this.#buyButtonsIntersectionObserver = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;

      // Only show sticky bar if buy buttons have been scrolled past (above viewport)
      if (!entry.isIntersecting && !this.#isStuck) {
        // Check if the element is above the viewport (scrolled past) or below (not yet reached)
        const rect = entry.target.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top < 0) {
          // Element is above viewport - show sticky bar
          this.#showStickyBar();
        }
        // If rect.top >= 0, element is below viewport - don't show sticky bar yet
      } else if (entry.isIntersecting && this.#isStuck) {
        this.#hiddenByBottom = false;
        this.#hideStickyBar();
      }
    });

    // Observer for footer visibility - hides sticky bar at page bottom
    this.#mainBottomObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry) return;

        if (entry.isIntersecting && this.#isStuck) {
          this.#hiddenByBottom = true;
          this.#hideStickyBar();
        } else if (!entry.isIntersecting && this.#hiddenByBottom) {
          // Footer out of view - check if we should show sticky bar again
          const rect = buyButtonsBlock.getBoundingClientRect();
          // Only show if buy buttons are above the viewport (scrolled past)
          if (rect.bottom < 0 || rect.top < 0) {
            this.#hiddenByBottom = false;
            this.#showStickyBar();
          }
        }
      },
      {
        rootMargin: '200px 0px 0px 0px',
      }
    );

    this.#buyButtonsIntersectionObserver.observe(buyButtonsBlock);
    this.#mainBottomObserver.observe(footer);
    this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
  }

  // Public action handlers
  /**
   * Handles the add to cart button click in the sticky bar
   */
  handleAddToCartClick = async () => {
    if (!this.#targetAddToCartButton) return;

    // Check if sticky button or target button is login to purchase
    const buttonText = this.refs.addToCartButton?.textContent?.trim().toLowerCase() || '';
    const targetText = this.#targetAddToCartButton.textContent?.trim().toLowerCase() || '';
    const loginToPurchaseTranslation = Theme.translations?.login_to_purchase?.trim().toLowerCase() || 'login to purchase';
    
    const isLoginToPurchase = buttonText === loginToPurchaseTranslation || 
                              buttonText.includes('login to purchase') || 
                              buttonText.includes('login to purchease') ||
                              targetText === loginToPurchaseTranslation ||
                              targetText.includes('login to purchase') ||
                              targetText.includes('login to purchease') ||
                              this.refs.addToCartButton?.dataset.splusLoginRequired === 'true' ||
                              this.#targetAddToCartButton.dataset.splusLoginRequired === 'true';

    const joinToPurchaseTranslation = Theme.translations?.join_to_purchase?.trim().toLowerCase() || 'join s/plus to purchase';
    const isJoinSplusToPurchase = buttonText === joinToPurchaseTranslation ||
                                  targetText === joinToPurchaseTranslation ||
                                  this.refs.addToCartButton?.dataset.splusJoinRequired === 'true' ||
                                  this.#targetAddToCartButton?.dataset.splusJoinRequired === 'true';

    if (isJoinSplusToPurchase) {
      const a = document.createElement('a');
      a.href = '#account-info';
      document.body.appendChild(a);
      a.click();
      a.remove();
      return;
    }

    if (isLoginToPurchase) {
      const drawer = document.querySelector('account-drawer-component');
      if (drawer && typeof drawer.openWithTab === 'function') {
        drawer.openWithTab('login');
      } else if (drawer && typeof drawer.open === 'function') {
        drawer.open();
      }
      return;
    }

    // Mirror the main button: it is the source of truth for what a tap can do
    const main = this.#getMainButton();
    if (!main) return;
    this.#targetAddToCartButton = main;
    if (main.disabled) return;

    if (!this.#isSizeSelected()) {
      this.#promptForSize();
      return;
    }

    // Only a tap that actually submits the form is an add. Taps the main button
    // handles itself (choose a store, quantity limit, validation) are not.
    const form = main.closest('form');
    const previousPuppet = main.dataset.puppet;
    let submitted = false;
    const onSubmit = () => {
      submitted = true;
    };
    form?.addEventListener('submit', onSubmit, { capture: true, once: true });

    main.dataset.puppet = 'true';
    main.click();
    form?.removeEventListener('submit', onSubmit, { capture: true });

    if (!submitted) {
      if (previousPuppet === undefined) delete main.dataset.puppet;
      else main.dataset.puppet = previousPuppet;
      return;
    }

    this.#pendingAdd = true;
    clearTimeout(this.#pendingAddTimeout);
    this.#pendingAddTimeout = window.setTimeout(() => {
      this.#pendingAdd = false;
    }, 10000);
  };

  /**
   * Plays the added feedback once the cart has confirmed the add.
   */
  #playAddedAnimation = async () => {
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    if (this.refs.addToCartButton.dataset.added !== 'true') {
      this.refs.addToCartButton.dataset.added = 'true';
    }

    if (!cartIcon || !this.refs.addToCartButton || !this.refs.productImage) return;
    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    flyToCartElement.classList.add('fly-to-cart--sticky');
    flyToCartElement.style.setProperty('background-image', `url(${this.refs.productImage.src})`);
    flyToCartElement.useSourceSize = 'true';
    flyToCartElement.source = this.refs.productImage;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);

    await onAnimationEnd([this.refs.addToCartButton, flyToCartElement]);
    this.#resetTimeout = setTimeout(() => {
      this.refs.addToCartButton.removeAttribute('data-added');
    }, 800);
  };

  /**
   * Handles variant update events
   * @param {CustomEvent} event - The variant update event
   */
  #handleVariantUpdate = (event) => {
    if (event.detail.data.productId !== this.dataset.productId) return;

    const variant = event.detail.resource;

    // Get the new sticky add to cart HTML from the server response
    const newStickyAddToCart = event.detail.data.html.querySelector('sticky-add-to-cart');
    if (!newStickyAddToCart) return;

    const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
    if (!newStickyBar) return;

    // Store current visibility state before morphing
    const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
    const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

    // Morph the entire sticky bar content
    morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

    // Restore visibility state after morphing
    this.refs.stickyBar.setAttribute('data-stuck', currentStuck);
    this.dataset.variantAvailable = variantAvailable;

    // Update the dataset attributes with new variant info
    if (variant && variant.id) {
      this.dataset.currentVariantId = variant.id;
    }

    // Re-cache the target add to cart button after morphing
    this.#observeMainButton();
    this.#targetAddToCartButton = this.#getMainButton();

    if (variant == null) {
      this.#handleVariantUnavailable();
    }

    // The main button decides the label and state; the morph only brought the server's guess
    this.#syncFromMainButton();
    this.#syncSizeState();
  };

  /**
   * Handles variant selected events
   * @param {CustomEvent} event - The variant selected event
   */
  #handleVariantSelected = (event) => {
    // The variant update event will follow and handle all updates via morph
    // We just update the dataset here for tracking
    const variantId = event.detail.resource?.id;
    if (!variantId) return;
    this.dataset.currentVariantId = variantId;
  };

  /**
   * Updates the variant title based on selected options when the variant is unavailable
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';
    const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!variantTitleElement || !variantPicker) return;

    // Copy the rendered label rather than the input's raw value, so a converted size keeps all
    // four size system spans and follows the active size tab like the rest of the page.
    const parts = Array.from(variantPicker.querySelectorAll('input:checked'))
      .map((option) => {
        const input = /** @type {HTMLInputElement} */ (option);
        const label = input.closest('label')?.querySelector('[data-key="variant-option-text"]');
        const span = document.createElement('span');

        if (label) {
          span.append(...Array.from(label.childNodes, (node) => node.cloneNode(true)));
        } else {
          span.textContent = input.value;
        }

        return span;
      })
      .filter((span) => span.textContent?.trim() !== '');

    if (!parts.length) return;

    variantTitleElement.textContent = '';
    parts.forEach((span, index) => {
      if (index > 0) variantTitleElement.append(' / ');
      variantTitleElement.append(span);
    });
  };

  /**
   * Resets the puppet flag after a cart operation and, if this bar started
   * the add, confirms it with the added animation.
   * @param {CustomEvent} event - The cart update event
   */
  #handleCartUpdate = (event) => {
    this.#resetPuppet();
    if (!this.#pendingAdd) return;

    this.#clearPendingAdd();
    if (event.detail?.data?.didError) return;
    this.#playAddedAnimation();
  };

  /**
   * Resets the puppet flag and drops the pending add after a cart error
   * @param {CustomEvent} _event - The cart error event (unused)
   */
  #handleCartError = (_event) => {
    this.#resetPuppet();
    this.#clearPendingAdd();
  };

  #resetPuppet() {
    if (this.#targetAddToCartButton) {
      this.#targetAddToCartButton.dataset.puppet = 'false';
    }
  }

  #clearPendingAdd() {
    this.#pendingAdd = false;
    clearTimeout(this.#pendingAddTimeout);
  }

  /**
   * Handles quantity selector update events
   * @param {QuantitySelectorUpdateEvent} event - The quantity update event
   */
  #handleQuantityUpdate = (event) => {
    // Only respond to product page quantity selector updates, not cart drawer
    if (event.detail.cartLine) return;

    this.#currentQuantity = event.detail.quantity;
    this.#updateButtonText();
  };

  /**
   * Shows the sticky bar with animation
   */
  #showStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = true;
    stickyBar.dataset.stuck = 'true';
    // Lets the stylesheet lower the chat widget while the bar is showing
    document.documentElement.setAttribute('data-sticky-atc-visible', '');
    this.#syncChatClearance();
  }

  /**
   * Hides the sticky bar with animation
   */
  #hideStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = false;
    stickyBar.dataset.stuck = 'false';
    document.documentElement.removeAttribute('data-sticky-atc-visible');
    this.#syncChatClearance();
  }

  // Helper methods
  /**
   * Gets the product form element
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  /**
   * The Gorgias chat button is injected by the app after load, so look for it for a
   * while, then follow its size and position once it exists.
   */
  #watchChatWidget() {
    let attempts = 0;

    const find = () => {
      attempts += 1;
      const widget = document.getElementById('chat-button');

      if (widget) {
        clearInterval(this.#chatPollTimer);
        this.#chatWidget = widget;
        this.#chatResizeObserver = new ResizeObserver(this.#syncChatClearance);
        this.#chatResizeObserver.observe(widget);
        this.#chatMutationObserver = new MutationObserver(this.#syncChatClearance);
        this.#chatMutationObserver.observe(widget, { attributes: true, attributeFilter: ['style', 'class', 'width', 'height'] });
        this.#syncChatClearance();
      } else if (attempts > 40) {
        clearInterval(this.#chatPollTimer);
      }
    };

    this.#chatPollTimer = window.setInterval(find, 500);
    find();
  }

  /**
   * Stops the bar's button short of the chat widget so the two never overlap.
   * Measured, not assumed, so it follows whatever size and position the widget has.
   */
  #syncChatClearance = () => {
    const { stickyBar, addToCartButton } = this.refs;
    if (!stickyBar || !addToCartButton) return;

    const widget = this.#chatWidget;
    const widgetRect = widget?.getBoundingClientRect();
    const barStyle = getComputedStyle(stickyBar);
    const barRect = stickyBar.getBoundingClientRect();
    const barBottom = window.innerHeight - (parseFloat(barStyle.bottom) || 0);
    // The bar's resting size, without the lift added below, so measuring never feeds back into itself
    const restingHeight = stickyBar.offsetHeight - this.#chatLift;
    const restingPaddingBottom = (parseFloat(barStyle.paddingBottom) || 0) - this.#chatLift;
    const barTop = barBottom - restingHeight;
    const gap = 8;
    const minimumButtonWidth = 120;

    // Same place vertically (the bar's resting position, so this holds mid slide-in) and
    // far enough right to leave the button room.
    const overlapsBar = Boolean(
      widgetRect &&
        widgetRect.width > 0 &&
        widgetRect.bottom > barTop &&
        widgetRect.top < barBottom &&
        widgetRect.left - barRect.left > minimumButtonWidth
    );

    if (!overlapsBar || !widgetRect) {
      this.#chatLift = 0;
      stickyBar.style.removeProperty('--sticky-atc-padding-end');
      stickyBar.style.removeProperty('--sticky-atc-padding-bottom');
      return;
    }

    // Stop short of the widget, and sit on the widget's centre line rather than below it
    stickyBar.style.setProperty('--sticky-atc-padding-end', `${Math.round(barRect.right - (widgetRect.left - gap))}px`);

    const widgetCentre = widgetRect.top + widgetRect.height / 2;
    const paddingBottom = Math.max(restingPaddingBottom, barBottom - widgetCentre - addToCartButton.offsetHeight / 2);
    this.#chatLift = paddingBottom - restingPaddingBottom;
    stickyBar.style.setProperty('--sticky-atc-padding-bottom', `${Math.round(paddingBottom * 10) / 10}px`);
  };

  /**
   * Gets the main add to cart button, looked up fresh because the theme morphs it
   * @returns {HTMLButtonElement | null}
   */
  #getMainButton() {
    return this.#getProductForm()?.querySelector('[ref="addToCartButton"]') ?? null;
  }

  /**
   * Watches the product form so the bar follows whatever the main button turns into
   * (S/PLUS gates, not available online, click & collect, sold out).
   */
  #observeMainButton() {
    this.#mainButtonObserver?.disconnect();

    const productForm = this.#getProductForm();
    if (!productForm) return;

    this.#mainButtonObserver = new MutationObserver(() => {
      if (this.#syncFrame) return;
      this.#syncFrame = requestAnimationFrame(() => {
        this.#syncFrame = null;
        this.#syncFromMainButton();
      });
    });

    this.#mainButtonObserver.observe(productForm, {
      subtree: true,
      childList: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['disabled', 'data-splus-login-required', 'data-splus-join-required', 'data-cc-override-text'],
    });
  }

  /**
   * Copies the main button's label, enabled state and S/PLUS gate onto the bar.
   */
  #syncFromMainButton() {
    const main = this.#getMainButton();
    const button = this.refs.addToCartButton;
    if (!main || !button) return;

    this.#targetAddToCartButton = main;

    const container = main.closest('add-to-cart-component');
    const loginRequired = main.dataset.splusLoginRequired === 'true' || container?.dataset.splusLoginRequired === 'true';
    const joinRequired = main.dataset.splusJoinRequired === 'true' || container?.dataset.splusJoinRequired === 'true';
    const gated = loginRequired || joinRequired;

    if (loginRequired) button.dataset.splusLoginRequired = 'true';
    else delete button.dataset.splusLoginRequired;
    if (joinRequired) button.dataset.splusJoinRequired = 'true';
    else delete button.dataset.splusJoinRequired;

    const label = (main.querySelector('.add-to-cart-text')?.textContent ?? main.textContent ?? '').replace(/\s+/g, ' ').trim();
    const labelElement = button.querySelector('.sticky-add-to-cart__label');
    if (label && labelElement && labelElement.textContent !== label) {
      labelElement.textContent = label;
    }

    const addLabels = new Set(
      [this.dataset.addLabel, Theme.translations?.add_to_cart, 'Add to bag', 'Collect in store']
        .filter(Boolean)
        .map((text) => String(text).toLowerCase())
    );
    const showPrice = !main.disabled && !gated && addLabels.has(label.toLowerCase());

    button.dataset.showPrice = String(showPrice);
    button.disabled = main.disabled && !gated;
    this.dataset.variantAvailable = String(!main.disabled || gated);
    this.#updateButtonText();
  }

  /**
   * Whether a size is chosen, using the same check as the main add to cart button
   * @returns {boolean}
   */
  #isSizeSelected() {
    const target = this.closest('.shopify-section');
    const sizeFieldset = target?.querySelector('fieldset[data-is-size="true"]');
    const sizeSelect = /** @type {HTMLSelectElement | null | undefined} */ (target?.querySelector('select[data-is-size="true"]'));

    if (sizeFieldset && !sizeFieldset.querySelector('input:checked')) return false;
    if (sizeSelect && (sizeSelect.value === '' || (sizeSelect.selectedIndex === 0 && sizeSelect.options[0]?.disabled))) {
      return false;
    }
    return true;
  }

  /**
   * Until a size is chosen the bar would show the first variant's size as if it were picked
   */
  #syncSizeState() {
    const variantElement = this.querySelector('.sticky-add-to-cart__variant');
    if (!variantElement || this.#isSizeSelected()) return;

    variantElement.textContent = Theme.translations?.select_size || 'Select size';
  }

  /**
   * Takes the shopper to the size picker with the same error the main button shows
   */
  #promptForSize() {
    const target = this.closest('.shopify-section');
    const productForm = /** @type {any} */ (this.#getProductForm());
    productForm?.showSizeError?.();

    const picker = target?.querySelector('[data-is-size="true"]') ?? target?.querySelector('.size-validation-error');
    picker?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  /**
   * Gets the initial quantity from the data attribute
   */
  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1') || 1;
    this.#updateButtonText();
  }

  /**
   * Updates the button text to include quantity
   */
  #updateButtonText() {
    const { addToCartButton, quantityDisplay, quantityNumber } = this.refs;

    const available = !addToCartButton.disabled && addToCartButton.dataset.showPrice !== 'false';

    // Update the quantity number
    quantityNumber.textContent = this.#currentQuantity.toString();

    // Show/hide the quantity display based on availability and quantity
    if (available && this.#currentQuantity > 1) {
      quantityDisplay.style.display = 'inline';
    } else {
      quantityDisplay.style.display = 'none';
    }
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
